import { Injectable, OnDestroy } from "@angular/core";
import { IWebVoucherLandingPage, IWebVoucher, IWebVoucherSelectorService } from "./webVoucherSelector.facade";
import { BehaviorSubject, Observable, Subscription } from "rxjs";
import { WebvoucherDataModel, Webvoucher_LandingPage, WebshopSettingsDataModel2, WebshopSettingsDMKey } from "../../shared/models";
import { CoreLogService } from "../../services/log.service";
import { CircularInjectionHelper } from "../../services/circularInjectionHelper";
import { ObjectObserverFactory } from "../../services/objectObserverFactory";
import { IObjectObserver } from "../../services/IObjectObserver";
import { ContentType } from "../../shared/contentTypes";
import Utils from "../../shared/utils";

export class WebVoucherLandingPage implements IWebVoucherLandingPage {
  public get label(): string {
    return this._model.label;
  }
  public get description(): string {
    return this._model.subtext;
  }
  public get hasImage(): boolean {
    return this._model.last_image_update_at_ymd > 0;
  }
  public get image(): string {
    return this._model.image_photo;
  }

  /** Basis Datenmodel */
  private _model: Webvoucher_LandingPage

  public constructor(model: Webvoucher_LandingPage) {
    const me = this;
    me._model = model;
  }
}

export class WebVoucherCustom implements IWebVoucher {
  public get id(): number {
    return this._model.id;
  }
  public get label(): string {
    return this._model.label;
  }
  public get price(): number {
    return this._model.is_free_amount ? 0 : this._model.fix_amount;
  }
  public get hasImage(): boolean {
    return this._model.last_image_update_at_ymd > 0;
  }
  public get image(): string {
    return this._model.image_photo;
  }

  /** Basis Datenmodel */
  private _model: WebvoucherDataModel

  public constructor(model: WebvoucherDataModel) {
    const me = this;
    me._model = model;
  }
}

@Injectable()
export class WebVoucherSelectorService implements IWebVoucherSelectorService, OnDestroy {

  // observer
  private _webshopSettingsObsServer: IObjectObserver<WebshopSettingsDataModel2>;
  private _webvoucherCustomObsServer: IObjectObserver<WebvoucherDataModel>;

  // subscriptions
  private _webshopSettingsChangedSub: Subscription;
  private _webvoucherCustomChangedSub: Subscription;

  constructor(private _coreLogService: CoreLogService,
    objObsInjectHelper: CircularInjectionHelper) {
    const me = this;

    me._webshopSettingsObsServer = ObjectObserverFactory.GetObjectObserverByInjectionHelper<WebshopSettingsDataModel2>(ContentType.WebshopSettings, objObsInjectHelper);
    me.loadWebVoucherLandingPage(); // initial load - no await
    me._webshopSettingsChangedSub = me._webshopSettingsObsServer.ObserveChanges.subscribe((_) => {
      me.loadWebVoucherLandingPage(); // no await
    });

    me._webvoucherCustomObsServer = ObjectObserverFactory.GetObjectObserverByInjectionHelper<WebvoucherDataModel>(ContentType.Webvoucher_Custom, objObsInjectHelper);
    me.loadWebVouchers(true); // initial load - no await 
    me._webvoucherCustomChangedSub = me._webvoucherCustomObsServer.ObserveChanges.subscribe((_) => {
      me.loadWebVouchers(false); // no await 
    });

  }

  ngOnDestroy(): void {
    const me = this;
    if (me._webshopSettingsChangedSub) {
      me._webshopSettingsChangedSub.unsubscribe();
    }
    if (me._webvoucherCustomChangedSub) {
      me._webvoucherCustomChangedSub.unsubscribe();
    }
  }

  /** hält die referenz auf die Webgutschien landingpage */
  private _webVoucher_landingPage: WebVoucherLandingPage = null;
  private _webVoucher_landingPageSubject: BehaviorSubject<IWebVoucherLandingPage> = new BehaviorSubject<IWebVoucherLandingPage>(null);
  public get webVoucher_landingPage$(): Observable<IWebVoucherLandingPage> {
    return this._webVoucher_landingPageSubject.asObservable();
  }

  /** hält die referenz auf die Webgutschiene */
  private _webVouchers: WebVoucherCustom[] = [];
  private _webVouchersSubject: BehaviorSubject<ReadonlyArray<IWebVoucher>> = new BehaviorSubject<ReadonlyArray<IWebVoucher>>([]);
  public get webVouchers$(): Observable<ReadonlyArray<IWebVoucher>> {
    return this._webVouchersSubject.asObservable();
  }

  /** ermittelt die Webgutschein Landingpage */
  private async loadWebVoucherLandingPage(): Promise<void> {
    const me = this;
    try {
      const webshopSettings = await me._webshopSettingsObsServer.GetObject(WebshopSettingsDMKey);
      let webVoucher_landingPage = null;
      if (webshopSettings.webvoucher.landing_page) {
        webVoucher_landingPage = new WebVoucherLandingPage(webshopSettings.webvoucher.landing_page);
      }
      if (!Utils.IsEqual(me._webVoucher_landingPage, webVoucher_landingPage)) {
        me._webVoucher_landingPage = webVoucher_landingPage;
        me._webVoucher_landingPageSubject.next(me._webVoucher_landingPage);
      }
    } catch (error) {
      me._coreLogService.error("WebVoucherSelectorService.loadWebVoucherLandingPage", "catch", error);
    }
  }

  /** ermittelt die Webgutscheine */
  private async loadWebVouchers(initial_load: boolean): Promise<void> {
    const me = this;
    try {
      const webvouchers = await me._webvoucherCustomObsServer.GetObjectsByFilter((webvoucher) => webvoucher.active && !webvoucher.deleted, initial_load);
      let webVouchers = [];
      webvouchers.sort((a, b) => a.sort_no - b.sort_no).forEach(webvoucher => {
        webVouchers.push(new WebVoucherCustom(webvoucher));
      });
      if (!Utils.IsEqual(me._webVouchers, webVouchers)) {
        me._webVouchers = webVouchers;
        me._webVouchersSubject.next(me._webVouchers);
      }
    } catch (error) {
      me._coreLogService.error("WebVoucherSelectorService.loadWebVouchers", "catch", error);
    }
  }

}